import { Container } from 'src/components/common/Container'
import {
  ChipsActiveStyle,
  ChipsDefaultStyle,
  ChipsItemsBigStyle,
  ChipsItemStyle,
  ChipsListStyle,
} from 'src/components/common/Chips'
import { CategoryListSpace } from 'src/types/api'
import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import useContentsTopLevelDetection from 'src/hooks/useContentsTopLevelDetection.hook'
import { layoutMaxSize, ZIndex } from 'src/styles/mixin'
import { BrandListPageProps } from 'src/pages/BrandListPage'
import { Link as ReactScrollLink } from 'react-scroll'
import { CustomSwiper } from 'src/components/common/CustomSwiper'
import { SwiperRef } from 'swiper/swiper-react'
import useLocalStorage from 'src/hooks/useLocalStorage.hook'

type OmitCategoryImage = Omit<CategoryListSpace.CategoryInfoType, 'CategoryImage'>

const BrandListChipsList = ({
  categoryListData,
  categorySelectHandler,
  activeCategory,
}: Pick<BrandListPageProps, 'categoryListData' | 'categorySelectHandler' | 'activeCategory'>) => {
  const { contentsRef, detector } = useContentsTopLevelDetection(99)
  const swiperRef = useRef<SwiperRef>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [prevBrandCategory, _setPrevBrandCategory, removePrevBrandCategory] =
    useLocalStorage<number>('prev_brand_category', activeCategory)

  const swiperClickTrigger = (categoryNumber: number) => {
    const categoryOrder = categoryListData?.findIndex((w) => w.CategoryNumber === categoryNumber)
    categorySelectHandler(categoryNumber)

    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(categoryOrder)
    }
  }

  useEffect(() => {
    if (!!prevBrandCategory) {
      swiperClickTrigger(prevBrandCategory)
      removePrevBrandCategory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <S.BrandListChipsListContainer $option="flex_column" ref={contentsRef}>
      <CustomSwiper.Wrap
        type="default"
        gap={6}
        ref={swiperRef}
        $wrapstyle={S.BrandListChipsSwiperStyle(detector)}
      >
        {categoryListData?.map(({ CategoryName, CategoryNumber }: OmitCategoryImage) => {
          const chipsActive = activeCategory === CategoryNumber
          return (
            <CustomSwiper.Item
              key={CategoryNumber}
              $itemstyle={S.BrandListChipsSwiperItemsStyle(chipsActive)}
            >
              <S.BrandListChipsLink
                spy
                smooth
                isDynamic
                offset={-100}
                duration={300}
                to={`${CategoryNumber}`}
                onSetActive={(to: string) => swiperClickTrigger(Number(to))}
              >
                {CategoryName}
              </S.BrandListChipsLink>
            </CustomSwiper.Item>
          )
        })}
      </CustomSwiper.Wrap>
    </S.BrandListChipsListContainer>
  )
}
export default BrandListChipsList

const S = {
  BrandListChipsListContainer: styled(Container)`
    ${layoutMaxSize};
    height: 60px;
  `,
  BrandListChipsSwiperStyle: ($position_change: boolean) => css`
    ${ChipsListStyle()};
    ${layoutMaxSize};
    padding: 10px 20px 6px 20px;
    position: ${$position_change ? 'fixed' : 'absolute'};
    top: ${$position_change ? '40px;' : 'unset'};
    background: var(--white);
    z-index: ${ZIndex.CategoryChips};
    ${$position_change &&
    css`
      border-bottom: 1px solid #ededed;
    `}
  `,
  BrandListChipsSwiperItemsStyle: (active?: boolean) => css`
    ${ChipsItemStyle};
    ${ChipsItemsBigStyle};
    ${active ? ChipsActiveStyle() : ChipsDefaultStyle()};
    padding: 0 !important;
  `,
  BrandListChipsLink: styled(ReactScrollLink)`
    border-radius: 100px;
    width: max-content;
    height: 44px;
    display: flex;
    align-items: center;
    padding: 10px 16px;
    &.active {
      > button {
        color: var(--white);
        background: var(--black-90);
        box-sizing: border-box;
      }
    }
  `,
}
